import React, { useState, useEffect, useCallback, useRef } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import ModalNuevaFactura from './ModalNuevaFactura';
import ModalCobros from './ModalCobros';
import ModalCorregirFactura from './ModalCorregirFactura';
import PantallaCargaMedica from '../common/PantallaCargaMedica';
import socket from '../../services/socket';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

import {
    AddCircle as AddIcon,
    PictureAsPdf as PdfIcon,
    Payments as CashIcon,
    CreditCard as CardIcon,
    AccountBalance as BankIcon,
    AccountBalanceWallet as WalletIcon
} from '@mui/icons-material';
import { euros } from '../../utils/formatos';

const RESUMEN_VACIO = {
    historico: { efectivo: 0, tarjeta: 0, transferencia: 0, total: 0, num: 0 },
    hoy: { efectivo: 0, tarjeta: 0, transferencia: 0, total: 0, num: 0 },
    pendientes: { importe: 0, num: 0 }
};

const ESTILO_ESTADO = {
    pagada: 'bg-emerald-100 text-emerald-700',
    parcial: 'bg-sky-100 text-sky-700',
    pendiente: 'bg-amber-100 text-amber-700',
    anulada: 'bg-rose-100 text-rose-700'
};

const ESTILO_PAGO = {
    efectivo: 'bg-emerald-100 text-emerald-700',
    tarjeta: 'bg-sky-100 text-sky-700',
    transferencia: 'bg-violet-100 text-violet-700'
};


// Estado del envío a Hacienda (VERI*FACTU) de cada factura
const VERIFACTU = {
    por_enviar: { texto: 'Por enviar', estilo: 'bg-amber-50 text-amber-700' },
    pendiente: { texto: 'Enviada a Hacienda', estilo: 'bg-sky-50 text-sky-700' },
    correcto: { texto: 'Aceptada por Hacienda', estilo: 'bg-emerald-50 text-emerald-700' },
    aceptado_con_errores: { texto: 'Aceptada con errores', estilo: 'bg-amber-100 text-amber-800' },
    incorrecto: { texto: 'Rechazada por Hacienda', estilo: 'bg-rose-100 text-rose-700' },
    rechazada: { texto: 'No enviada: revisar datos', estilo: 'bg-rose-100 text-rose-700' }
};
const URL_PLANES = 'https://klyndu.com/contacto?tipo=precios';
const fechaLarga = (iso) => (iso ? new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }) : '');

// Métodos con los que se ha cobrado (una factura puede pagarse con varios)
const metodosDe = (f) => {
    const vigentes = (f.pagos || []).filter(p => !p.anulado).map(p => p.metodo);
    return vigentes.length ? [...new Set(vigentes)] : [f.metodoPago].filter(Boolean);
};

const Facturacion = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { showToast } = useToast();
    const toastRef = useRef(showToast);
    toastRef.current = showToast;

    const rol = user?.role || user?.rol;
    const esAdmin = rol === 'admin';
    // Mismo criterio que el backend: anular es cosa de dirección
    const puedeAnular = ['supermedico', 'admin'].includes(rol);

    const [facturas, setFacturas] = useState([]);
    const [totalFacturas, setTotalFacturas] = useState(0);
    const [resumen, setResumen] = useState(RESUMEN_VACIO);
    const [empresa, setEmpresa] = useState(null); // null = cargando
    const [loading, setLoading] = useState(true);
    const [openModal, setOpenModal] = useState(false);
    const [confirmandoAnular, setConfirmandoAnular] = useState(null);
    const [procesandoId, setProcesandoId] = useState(null);
    const [cobrando, setCobrando] = useState(null); // factura abierta en la ventana de cobros
    const [verifactu, setVerifactu] = useState(null); // plan y estado de VeriFactu de la clínica
    const [corrigiendo, setCorrigiendo] = useState(null); // factura rechazada abierta para corregir

    const cargarFacturas = useCallback(async () => {
        try {
            setLoading(true);
            // Los totales los calcula el servidor sobre TODAS las facturas cobradas
            // (antes se sumaba en el navegador solo la primera página de 50 y se
            // contaban también las pendientes de cobro).
            const [lista, totales] = await Promise.all([
                api.get('/facturas?limit=100'),
                api.get('/facturas/resumen')
            ]);
            const listaFacturas = Array.isArray(lista) ? lista : (lista?.data || []);
            setFacturas(listaFacturas);
            setTotalFacturas(lista?.pagination?.totalDocs ?? listaFacturas.length);
            setResumen(totales?.historico ? totales : RESUMEN_VACIO);
        } catch (err) {
            console.error('Error cargando facturas:', err);
            toastRef.current(err.message || 'No se pudieron cargar las facturas.', 'error');
            setFacturas([]);
            setResumen(RESUMEN_VACIO);
        } finally {
            setLoading(false);
        }
    }, []);

    const cargarEmpresa = useCallback(async () => {
        try {
            const res = await api.get('/empresa');
            setEmpresa(res || { configurada: false });
        } catch {
            setEmpresa({ configurada: false });
        }
        try {
            setVerifactu(await api.get('/verifactu'));
        } catch {
            setVerifactu(null);
        }
    }, []);

    // Reintentar el envío o volver a preguntar a Hacienda por una factura
    const actualizarVerifactu = async (factura) => {
        try {
            setProcesandoId(factura.id);
            const dto = await api.post(`/verifactu/facturas/${factura.id}/actualizar`);
            const estado = VERIFACTU[dto?.verifactu?.estado]?.texto;
            showToast(estado ? `Factura ${factura.numeroFactura}: ${estado.toLowerCase()}.` : 'Estado actualizado.', 'info');
            cargarFacturas();
            cargarEmpresa();
        } catch (err) {
            showToast(err.message || 'No se pudo consultar el estado.', 'error');
        } finally {
            setProcesandoId(null);
        }
    };

    const handleVerDescargarPDF = async (facturaId, numero) => {
        try {
            const response = await api.get(`/facturas/${facturaId}/pdf`, { responseType: 'blob' });
            const datosBinarios = response?.data || response;
            const blob = new Blob([datosBinarios], { type: 'application/pdf' });
            const urlTemporal = window.URL.createObjectURL(blob);

            const nuevaPestana = window.open(urlTemporal, '_blank');
            if (!nuevaPestana) {
                const enlaceDescarga = document.createElement('a');
                enlaceDescarga.href = urlTemporal;
                enlaceDescarga.download = `${numero || `factura-${facturaId}`}.pdf`;
                document.body.appendChild(enlaceDescarga);
                enlaceDescarga.click();
                document.body.removeChild(enlaceDescarga);
            }
            // Margen amplio: algunos visores leen el blob después de abrir la pestaña
            setTimeout(() => window.URL.revokeObjectURL(urlTemporal), 60000);
        } catch (error) {
            console.error('Error al obtener el documento PDF:', error);
            showToast('No se pudo generar el PDF de la factura.', 'error');
        }
    };

    const handleAnular = async (factura) => {
        try {
            setProcesandoId(factura.id);
            await api.delete(`/facturas/${factura.id}`);
            showToast(`Factura ${factura.numeroFactura || ''} anulada.`, 'info');
            setConfirmandoAnular(null);
            cargarFacturas();
        } catch (err) {
            showToast(err.message || 'No se pudo anular la factura.', 'error');
        } finally {
            setProcesandoId(null);
        }
    };

    useEffect(() => {
        cargarFacturas();
        cargarEmpresa();

        if (!socket.connected) socket.connect();

        const recargar = () => cargarFacturas();
        socket.on('nueva_factura_emitida', recargar);
        socket.on('factura_estado_actualizado', recargar);

        return () => {
            socket.off('nueva_factura_emitida', recargar);
            socket.off('factura_estado_actualizado', recargar);
        };
    }, [cargarFacturas, cargarEmpresa]);

    if (loading && facturas.length === 0 && empresa === null) {
        return (
            <div className="min-h-screen p-4 md:p-8 bg-slate-50">
                <PantallaCargaMedica variante="facturas" />
            </div>
        );
    }

    const fiscalConfigurada = Boolean(empresa?.configurada);
    const esencial = verifactu?.plan === 'esencial';
    const bloqueadaPorVerifactu = !esencial && verifactu && verifactu.estado !== 'activo' && verifactu.obligatorioYa;
    const puedeEmitir = esencial || (fiscalConfigurada && !bloqueadaPorVerifactu);

    const tarjetas = [
        { titulo: 'EFECTIVO', valor: resumen.historico.efectivo, icono: <CashIcon fontSize="large" />, color: 'bg-emerald-100 text-emerald-700' },
        { titulo: 'TARJETA (TPV)', valor: resumen.historico.tarjeta, icono: <CardIcon fontSize="large" />, color: 'bg-sky-100 text-sky-700' },
        { titulo: 'TRANSFERENCIA', valor: resumen.historico.transferencia, icono: <BankIcon fontSize="large" />, color: 'bg-violet-100 text-violet-700' }
    ];

    return (
        <div className="min-h-screen p-4 md:p-8 bg-slate-50">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight">Caja y Facturación</h1>
                    <p className="text-sm font-semibold text-slate-500">Facturas, cobros y caja del día</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    {esAdmin && !esencial && (
                        <button onClick={() => navigate('/facturacion/verifactu')} className="px-5 py-3 text-sm font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition-colors">
                            VeriFactu
                        </button>
                    )}
                    <button
                        onClick={() => setOpenModal(true)}
                        disabled={!puedeEmitir}
                        title={puedeEmitir ? '' : (fiscalConfigurada ? 'Hay que activar VeriFactu' : 'Faltan los datos fiscales de la clínica')}
                        className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold px-6 py-3 rounded-xl transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <AddIcon fontSize="small" /> {esencial ? 'Nuevo documento provisional' : 'Emitir Factura'}
                    </button>
                </div>
            </div>

            {/* 🏢 Sin identidad fiscal el backend rechaza cualquier emisión */}
            {empresa && !fiscalConfigurada && !esencial && (
                <div className="flex flex-wrap items-center justify-between gap-4 mb-6 p-4 rounded-2xl bg-rose-50 border border-rose-200">
                    <div className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
                        <p className="text-sm font-semibold text-rose-800">
                            <span className="font-black">No se pueden emitir facturas:</span> faltan los datos fiscales de la clínica (NIF, razón social y domicilio).
                            {!esAdmin && ' Pídeselo al administrador.'}
                        </p>
                    </div>
                    {esAdmin && (
                        <button onClick={() => navigate('/admin/datos-fiscales')} className="px-4 py-2 text-sm font-bold text-white bg-rose-600 hover:bg-rose-700 rounded-xl transition-colors">
                            Configurar datos fiscales
                        </button>
                    )}
                </div>
            )}

            {/* Plan y VeriFactu: qué pasa con las facturas que se emitan */}
            {verifactu && (() => {
                const caja = (tono, contenido, boton) => (
                    <div className={`flex flex-wrap items-center justify-between gap-4 mb-8 p-4 rounded-2xl border ${tono}`}>
                        <p className="text-xs font-semibold leading-relaxed">{contenido}</p>
                        {boton}
                    </div>
                );
                const botonVerifactu = (texto) => esAdmin && (
                    <button onClick={() => navigate('/facturacion/verifactu')} className="px-4 py-2 text-xs font-bold text-white bg-slate-900 hover:bg-slate-800 rounded-xl">{texto}</button>
                );
                if (esencial) {
                    return caja('bg-amber-50 border-amber-200 text-amber-900',
                        <><span className="font-black">Plan Esencial:</span> se crean documentos provisionales, sin valor fiscal (no son facturas ni se envían a Hacienda). Para emitir facturas, pásate al plan Completo.</>,
                        esAdmin && <a href={URL_PLANES} target="_blank" rel="noreferrer" className="px-4 py-2 text-xs font-bold text-white bg-slate-900 hover:bg-slate-800 rounded-xl">Pedir el plan Completo</a>);
                }
                if (verifactu.estado === 'activo') {
                    return caja(verifactu.facturas.porRevisar > 0 ? 'bg-rose-50 border-rose-200 text-rose-900' : 'bg-emerald-50 border-emerald-200 text-emerald-900',
                        <>
                            <span className="font-black">VeriFactu activado:</span> las facturas se envían a Hacienda al emitirlas.
                            {verifactu.entorno === 'test' && <span className="font-black text-amber-800"> Entorno de pruebas, sin validez fiscal.</span>}
                            {verifactu.facturas.porRevisar > 0 && <span className="font-black"> Hay {verifactu.facturas.porRevisar} factura(s) con errores: revísalas abajo.</span>}
                        </>);
                }
                if (verifactu.territorio === 'foral') return null;
                if (verifactu.obligatorioYa) {
                    return caja('bg-rose-50 border-rose-200 text-rose-900',
                        <><span className="font-black">No se pueden emitir facturas:</span> VeriFactu ya es obligatorio y no está activado.{!esAdmin && ' Pídeselo al administrador.'}</>,
                        botonVerifactu('Activar VeriFactu'));
                }
                return caja('bg-sky-50 border-sky-200 text-sky-900',
                    <><span className="font-black">VeriFactu no está activado:</span> las facturas aún no se envían a Hacienda. Será obligatorio desde el {fechaLarga(verifactu.obligatorioDesde)}.</>,
                    botonVerifactu('Configurar VeriFactu'));
            })()}

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 mb-3">
                {tarjetas.map(t => (
                    <div key={t.titulo} className="flex items-center gap-4 p-6 bg-white border border-slate-200 rounded-3xl shadow-sm">
                        <div className={`flex items-center justify-center w-14 h-14 rounded-2xl ${t.color}`}>{t.icono}</div>
                        <div>
                            <p className="text-xs font-bold text-slate-500 tracking-wider">{t.titulo}</p>
                            <h3 className="text-2xl font-black text-slate-900">{euros(t.valor)}</h3>
                        </div>
                    </div>
                ))}
                <div className="flex items-center gap-4 p-6 bg-slate-900 border border-slate-800 rounded-3xl shadow-lg">
                    <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-white/10 text-white">
                        <WalletIcon fontSize="large" />
                    </div>
                    <div>
                        <p className="text-xs font-bold text-slate-400 tracking-wider">TOTAL COBRADO</p>
                        <h3 className="text-2xl font-black text-white">{euros(resumen.historico.total)}</h3>
                    </div>
                </div>
            </div>
            <p className="mb-8 text-xs font-semibold text-slate-500">
                Hoy: <span className="font-black text-slate-700">{euros(resumen.hoy.total)}</span> en {resumen.hoy.num} factura(s)
                {resumen.pendientes.num > 0 && (
                    <> · Pendiente de cobro: <span className="font-black text-amber-700">{euros(resumen.pendientes.importe)}</span> en {resumen.pendientes.num} factura(s)</>
                )}
                {' '}· Las facturas anuladas no suman.
            </p>

            <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse min-w-[960px]">
                        <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="px-6 py-4 text-xs font-bold tracking-wider text-slate-500 uppercase">Nº / Fecha</th>
                            <th className="px-6 py-4 text-xs font-bold tracking-wider text-slate-500 uppercase">Paciente</th>
                            <th className="px-6 py-4 text-xs font-bold tracking-wider text-slate-500 uppercase">Concepto</th>
                            <th className="px-6 py-4 text-xs font-bold tracking-wider text-slate-500 uppercase">Pago</th>
                            <th className="px-6 py-4 text-xs font-bold tracking-wider text-slate-500 uppercase">Estado</th>
                            <th className="px-6 py-4 text-xs font-bold tracking-wider text-slate-500 uppercase text-right">Importe</th>
                            <th className="px-6 py-4 text-xs font-bold tracking-wider text-slate-500 uppercase text-center">Acciones</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                        {loading && facturas.length === 0 ? (
                            [...Array(4)].map((_, i) => (
                                <tr key={i} className="animate-pulse bg-white">
                                    {[...Array(7)].map((__, j) => (
                                        <td key={j} className="px-6 py-4"><div className="h-4 bg-slate-200 rounded w-20"></div></td>
                                    ))}
                                </tr>
                            ))
                        ) : facturas.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="px-6 py-12 text-center">
                                    <p className="text-slate-500 font-bold italic">Todavía no se ha emitido ninguna factura.</p>
                                </td>
                            </tr>
                        ) : (
                            facturas.map((f) => {
                                const anulada = f.estado === 'anulada';
                                const fecha = f.fechaEmision || f.fechaCreacion;
                                return (
                                    <tr key={f.id} className={`transition-colors ${anulada ? 'bg-slate-50/60' : 'hover:bg-slate-50'}`}>
                                        <td className="px-6 py-4">
                                            <div className="font-mono text-xs font-black text-slate-800">{f.numeroFactura || '—'}</div>
                                            <div className="text-xs font-semibold text-slate-500">
                                                {fecha ? format(new Date(fecha), 'dd/MM/yyyy HH:mm') : '--/--'}
                                                {f.tipoFactura && <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-black">{f.tipoFactura}</span>}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 font-black text-slate-900">
                                            {f.paciente ? `${f.paciente.nombre} ${f.paciente.apellidos}` : '—'}
                                        </td>
                                        <td className="px-6 py-4 font-semibold text-slate-500 max-w-[220px] truncate" title={(f.conceptos || []).map(c => c.descripcion).join(', ')}>
                                            {f.conceptos?.[0]?.descripcion || f.descripcionOperacion || '—'}
                                            {f.conceptos?.length > 1 && <span className="text-slate-400"> (+{f.conceptos.length - 1})</span>}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-wrap gap-1">
                                                {metodosDe(f).map(m => (
                                                    <span key={m} className={`inline-flex items-center px-2 py-1 rounded text-[10px] font-black uppercase tracking-widest ${ESTILO_PAGO[m] || 'bg-slate-100 text-slate-600'}`}>{m}</span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center px-2 py-1 rounded text-[10px] font-black uppercase tracking-widest ${ESTILO_ESTADO[f.estado] || 'bg-slate-100 text-slate-600'}`}>
                                                {f.estado}
                                            </span>
                                            {f.estado === 'parcial' && <div className="mt-1 text-[11px] font-bold text-sky-700">Faltan {euros(f.pendiente)}</div>}
                                            {f.provisional && <div className="mt-1 text-[11px] font-bold text-slate-500">Provisional</div>}
                                            {f.verifactu && (() => {
                                                const anul = f.verifactu.anulacion;
                                                const clave = anul ? anul.estado : f.verifactu.estado;
                                                const v = VERIFACTU[clave] || VERIFACTU.pendiente;
                                                const texto = anul ? (anul.estado === 'correcto' ? 'Anulación aceptada' : `Anulación: ${v.texto.toLowerCase()}`) : v.texto;
                                                const mensaje = anul?.mensajeError || f.verifactu.mensajeError;
                                                const reintentable = ['por_enviar', 'pendiente'].includes(clave);
                                                const corregible = !anul && clave === 'rechazada' && !anulada;
                                                return (
                                                    <>
                                                        <div className="mt-1 flex items-center gap-1">
                                                            <span title={mensaje || ''} className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold ${v.estilo}`}>{texto}</span>
                                                            {reintentable && (
                                                                <button onClick={() => actualizarVerifactu(f)} disabled={procesandoId === f.id} className="text-[10px] font-bold text-slate-500 underline disabled:opacity-50">
                                                                    {procesandoId === f.id ? '…' : 'Actualizar'}
                                                                </button>
                                                            )}
                                                            {corregible && (
                                                                <button onClick={() => setCorrigiendo(f)} className="text-[10px] font-bold text-rose-700 underline">
                                                                    Corregir y reenviar
                                                                </button>
                                                            )}
                                                        </div>
                                                        {mensaje && ['rechazada', 'incorrecto', 'aceptado_con_errores'].includes(clave) && (
                                                            <div className="mt-1 max-w-[220px] text-[11px] font-semibold text-rose-700">{mensaje}</div>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </td>
                                        <td className={`px-6 py-4 text-right font-black ${anulada ? 'text-rose-500 line-through' : 'text-slate-900'}`}>
                                            {euros(f.total)}
                                        </td>
                                        <td className="px-6 py-4">
                                            {confirmandoAnular === f.id ? (
                                                <div className="flex items-center justify-center gap-2">
                                                    <span className="text-xs font-bold text-rose-700">¿Anular?</span>
                                                    <button onClick={() => handleAnular(f)} disabled={procesandoId === f.id} className="px-2.5 py-1 text-xs font-black text-white bg-rose-600 hover:bg-rose-700 rounded-lg disabled:opacity-50">
                                                        Sí, anular
                                                    </button>
                                                    <button onClick={() => setConfirmandoAnular(null)} className="px-2.5 py-1 text-xs font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg">
                                                        No
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-center gap-1">
                                                    <button onClick={() => handleVerDescargarPDF(f.id, f.numeroFactura)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Ver / Descargar PDF" aria-label="Ver o descargar PDF">
                                                        <PdfIcon fontSize="small" />
                                                    </button>
                                                    {!anulada && Number(f.total) > 0 && (
                                                        <button onClick={() => setCobrando(f)} className="px-2.5 py-1 text-xs font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg disabled:opacity-50">
                                                            {f.pendiente > 0 ? 'Cobrar' : 'Pagos'}
                                                        </button>
                                                    )}
                                                    {puedeAnular && !anulada && (
                                                        <button onClick={() => setConfirmandoAnular(f.id)} className="px-2.5 py-1 text-xs font-bold text-rose-600 hover:bg-rose-50 rounded-lg">
                                                            Anular
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                        </tbody>
                    </table>
                </div>
                {totalFacturas > facturas.length && (
                    <p className="px-6 py-3 text-xs font-semibold text-slate-500 border-t border-slate-100">
                        Mostrando las {facturas.length} facturas más recientes de {totalFacturas}. Los totales de arriba incluyen todas.
                    </p>
                )}
            </div>

            <ModalNuevaFactura open={openModal} onClose={() => setOpenModal(false)} onSuccess={() => { cargarFacturas(); cargarEmpresa(); }} provisional={esencial} />
            {corrigiendo && (
                <ModalCorregirFactura factura={corrigiendo} onClose={() => setCorrigiendo(null)}
                                      onCorregida={(dto, aceptada) => {
                                          // Si la vuelve a rechazar, la ventana sigue abierta con el motivo
                                          if (aceptada) {
                                              setCorrigiendo(null);
                                              showToast(`Factura ${dto.numeroFactura} corregida y enviada a Hacienda.`, 'success');
                                          }
                                          cargarFacturas();
                                          cargarEmpresa();
                                      }} />
            )}
            {cobrando && (
                <ModalCobros factura={cobrando} puedeAnular={puedeAnular}
                             onClose={() => setCobrando(null)}
                             onCambio={(dto) => { setCobrando(dto); cargarFacturas(); }} />
            )}
        </div>
    );
};

export default Facturacion;
